import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

/**
 * useLiveSocket — connects to /api/ws/live for real-time data refresh.
 * Uses a connection ID to prevent stale async attempts from creating orphan sockets.
 */
export const useLiveSocket = (onDataUpdate) => {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const connectionId = useRef(0);
  const [connected, setConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const onDataUpdateRef = useRef(onDataUpdate);
  onDataUpdateRef.current = onDataUpdate;

  const connect = useCallback(async (connId) => {
    // Abort if this connection attempt is stale
    if (connId !== connectionId.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let token;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/ws-token`, { withCredentials: true });
      token = data.token;
    } catch (err) {
      console.error('Live WS token error:', err.message);
      return;
    }
    if (!token || connId !== connectionId.current) return;

    const wsBase = BACKEND_URL || window.location.origin;
    const wsUrl = wsBase.replace(/^http/, 'ws') + '/api/ws/live';
    const ws = new WebSocket(wsUrl);

    // If stale by the time socket opens, close immediately
    if (connId !== connectionId.current) {
      ws.onopen = () => ws.close();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (connId !== connectionId.current) { ws.close(); return; }
      setConnected(true);
      ws.send(JSON.stringify({ token }));
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          setOnlineCount(data.online || 1);
        } else if (data.type === 'data_update') {
          const eventName = data.event || '';
          const user = data.data?.user || 'Someone';
          const board = eventName.includes('sale') ? 'sales'
            : eventName.includes('purchase') ? 'purchases'
            : eventName.includes('expense') ? 'expenses' : 'data';
          toast.info(`${user} updated ${board}`, { duration: 3000, id: `live-${board}` });
          if (onDataUpdateRef.current) onDataUpdateRef.current(data);
        }
      } catch (err) {
        console.error('Live WS parse error:', err.message);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (connId === connectionId.current) {
        reconnectTimer.current = setTimeout(() => connect(connId), 5000);
      }
    };

    ws.onerror = () => { ws.close(); };
  }, []);

  useEffect(() => {
    const connId = ++connectionId.current;
    connect(connId);
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 45000);

    return () => {
      // Increment to invalidate any pending async connect for THIS effect's connId
      connectionId.current = connId + 1;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connected, onlineCount };
};
